import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  // current,
  createSlice,
  isDraft,
  original
} from '@reduxjs/toolkit';

import _findIndex from 'lodash/findIndex';
import _head from 'lodash/first';
import _omit from 'lodash/omit';
import _set from 'lodash/set';

import {
  fetchTranscript as fetchTranscriptApi,
  updateTranscript as updateTranscriptApi
  // offset as offsetApi,
} from 'api/transcripts';

import {
  checkout
} from 'features/collections';

import {
  loadSpeakersVersion
} from 'features/speakers';

import {
  loadHighlightsVersion
} from 'features/highlights';

import {toast} from 'components/common/Toaster';

export const SAVE_ERROR_KINDS = {
  validation: 'validation',
  network: 'network'
};

// Slice
export const transcriptssAdapter = createEntityAdapter();
const initialState = transcriptssAdapter.getInitialState({
  pendingRequests: [],
  saveErrors: [],
  entities: {}
});

// Selectors
const sliceSelector = state => state.transcripts;
const {selectById} = transcriptssAdapter.getSelectors(sliceSelector);

export const selectCollectionTranscript = createSelector(
  [selectById],
  transcript => _omit(transcript, 'id')
);

export const selectCollectionWords = createSelector(
  [selectCollectionTranscript],
  ({words} = {}) => words || []
);

export const selectCollectionFirstWord = createSelector(
  [selectCollectionWords],
  _head
);

export const selectCollectionWordStartTimes = createSelector(
  [selectCollectionWords],
  words => words.map(word => word.start_time)
);

export const selectCollectionSaveErrors = createSelector(
  [state => sliceSelector(state).saveErrors, (state, collectionId) => collectionId],
  (saveErrors, collectionId) => saveErrors.filter(({arg}) => arg.id === collectionId)
);

export const selectHasPendingTranscriptSaves = state =>
  sliceSelector(state).pendingRequests.length > 0;

// Actions

export const fetchTranscriptVersion = createAsyncThunk(
  'transcripts/versions/fetchOne',
  async ({collectionId, locale, trimmed, pubkey}, {getState, dispatch}) => {

    try {
      // TODO: Clean up. Should we be checking collection state to get url or call for url each time or....?
      // refactored to accomodate >5MB block reponse from API for large transcripts.
      // const {data} = await fetchTranscriptApi({collectionId, locale})
      const {collections: {entities}} = getState();
      // get the transcript S3 URL and version from the current collection
      const {transcription, version} = entities[collectionId];
      const response = await fetchTranscriptApi(transcription, pubkey);

      const {data: {results}} = response;
      const data = results;

      data.words = data.items && Object.freeze(data.items);
      // console.log('LOADING SPEAKER and HIGHLIGHTS VERSION', data.speakers, data.bookmarks)
      await dispatch(loadSpeakersVersion({collectionId, data: data.speakers}));
      await dispatch(loadHighlightsVersion({collectionId, data: data.bookmarks}));

      delete data.bookmarks;
      delete data.speakers;
      delete data.items;

      data.versionId = version || '';
      return {
        id: collectionId,
        ...data
      };
    } catch (error) {
      toast.requestRefresh({caller: 'transcripts/versions/fetchOne'});
      console.log('unable to fetch transcript version', collectionId, error);
      // throw new Error('Unable to fetch transcripts')
    }
  }
);

export const fetchTranscript = createAsyncThunk(
  'transcripts/fetchOne',
  async ({collectionId, locale, trimmed, pubkey}, {getState}) => {
    try {
      // TODO: Clean up. Should we be checking collection state to get url or call for url each time or....?
      // refactored to accomodate >5MB block reponse from API for large transcripts.
      // const {data} = await fetchTranscriptApi({collectionId, locale})
      const {collections: {entities}} = getState();
      const {transcription} = entities[collectionId];

      // TODO: not sure why I am not getting the bon bversion url is coming
      const response = await fetchTranscriptApi(transcription, pubkey);

      const {data: {results}} = response;
      const data = results;

      // backward compatible
      if (data.items && !data.words) {
        data.words = data.items;
      }

      delete data.speakers;
      delete data.items;
      data.version = '';

      if (trimmed) {
        const bookmark = 1;
        const type = 'pronunciation';
        const confidence = 1;
        const lastTime = data.words[data.words.length - 1].end_time + 1;
        const speaker = data.words[data.words.length - 1].speaker;
        const skip = false;
        data.words.push({skip, bookmark, 'start_time':lastTime, 'end_time':lastTime, 'alternatives':[{confidence,'content':'[CONTENT', speaker}],type});
        data.words.push({skip, bookmark, 'start_time':lastTime + 1, 'end_time':lastTime + 1, 'alternatives':[{confidence,'content':'TRIMMED', speaker}],type});
        data.words.push({skip, bookmark, 'start_time':lastTime + 2, 'end_time':lastTime + 2, 'alternatives':[{confidence,'content':'-', speaker}],type});
        data.words.push({skip, bookmark, 'start_time':lastTime + 3, 'end_time':lastTime + 3, 'alternatives':[{confidence,'content':'UPGRADE', speaker}],type});
        data.words.push({skip, bookmark, 'start_time':lastTime + 4, 'end_time':lastTime + 4, 'alternatives':[{confidence,'content':'REQUIRED]', speaker}],type});
        data.words.push({skip, bookmark, 'start_time':lastTime + 5, 'end_time':lastTime + 5, 'alternatives':[{confidence,'content':'', speaker}],type});
      }
      if (data.words) data.words = Object.freeze(data.words);
      return {
        id: collectionId,
        ...data
      };
    } catch (error) {
      toast.requestRefresh({caller: 'transcripts/fetchOne'});
      console.log('unable to fetch transcripts', collectionId, error);
      // throw new Error('Unable to fetch transcripts')
    }
  }
);

const markOutOfOrder = (startTime) => {
  const el = document.querySelector(`.word[data-start_time="${startTime}"]`);
  if (el) el.classList.add('out-of-order');
  if (el?.previousSibling) el.previousSibling.classList.add('out-of-order');
  if (el?.previousSibling?.previousSibling) el.previousSibling.previousSibling.classList.add('out-of-order');
};

// The redesigned editor shows save failures in its status strip and
// SaveErrorToast, so the legacy toasts only fire for the legacy layout.
export const updateTranscript = createAsyncThunk(
  'transcripts/updateOne',
  async ({id, words, startTime, endTime, locale, redesigned}, {getState, dispatch, rejectWithValue}) => {
    try {
      const state = getState();
      dispatch(checkout({collectionId: id, id, uid: state.firebase.auth.uid}));
      const {data} = await updateTranscriptApi({collectionId: id, data: {words, startTime, endTime}, locale});
      return data;
    } catch (error) {
      console.log('could not complete patch request');

      const {status = null, data: response} = error?.response || {};
      const {message = '', data} = response || {};
      console.log(response);

      const {alternatives, startTime: wordStartTime, word} = data || {};
      const hasLocation = wordStartTime !== undefined && wordStartTime !== null;
      if (hasLocation) markOutOfOrder(wordStartTime);

      if (!redesigned) {
        if (error?.response) {
          let wordString = (alternatives || [])[0]?.content || word || '';
          if (wordString) wordString = ` around "${wordString}"`;
          toast.error('Issue saving updates. ' + message + wordString + '. See text highlighted in red to correct.', {autoClose: false, toastId: 'out-of-order-errro'});
        }

        toast.requestRefresh({caller: 'updateTranscript.rejected', autoClose: 5000});
      }

      console.log(message, data);

      return rejectWithValue({
        kind: hasLocation ? SAVE_ERROR_KINDS.validation : SAVE_ERROR_KINDS.network,
        status,
        wordStartTime: hasLocation ? wordStartTime : null
      });
    }
  }
);

export const retryFailedSaves = ({collectionId, kind}) => (dispatch, getState) =>
  Promise.all(
    selectCollectionSaveErrors(getState(), collectionId)
      .filter(saveError => !kind || saveError.kind === kind)
      .map(({arg}) => dispatch(updateTranscript(arg)))
  );

// A newer save over the same range supersedes an older failed one, so it
// never gets replayed on top of fresher edits.
const overlapsSaveRange = (a, b) =>
  a.id === b.id && a.startTime <= b.endTime && b.startTime <= a.endTime;

const clearOverlappingSaveErrors = (state, arg) => {
  state.saveErrors = state.saveErrors.filter(saveError => !overlapsSaveRange(saveError.arg, arg));
};

export const slice = createSlice({
  name: 'transcripts',
  initialState,
  reducers: {},
  extraReducers: builder => {
    const addRequestId = (state, {meta: {requestId}}) => {
      state.pendingRequests.push(requestId);
    };

    const removeRequestId = (state, {meta: {requestId}}) => {
      state.pendingRequests = state.pendingRequests.filter(id => id !== requestId);
    };

    builder
      .addCase(updateTranscript.fulfilled, (
        state,
        args
      ) => {
        // const {meta: {arg: {id, words, startTime, endTime}}} = args
        // const oldWords = selectCollectionWords({transcripts: state}, id)
        // const preSliceIndex = _findIndex(oldWords, ({start_time}) => start_time >= startTime)
        // const preSlice = oldWords.slice(0, preSliceIndex)
        // const postSliceIndex = _findIndex(oldWords, ({start_time}) => start_time >= endTime)
        // const postSlice = oldWords.slice(postSliceIndex)
        // _set(state, ['entities', id, 'words'], [...preSlice, ...words, ...postSlice])
        clearOverlappingSaveErrors(state, args.meta.arg);
        removeRequestId(state, args);
      })
      .addCase(updateTranscript.rejected, (state, args) => {
        const {meta: {arg, requestId}, payload} = args;
        const {kind = SAVE_ERROR_KINDS.network, status = null, wordStartTime = null} = payload || {};
        clearOverlappingSaveErrors(state, arg);
        state.saveErrors.push({requestId, arg, kind, status, wordStartTime});
        removeRequestId(state, args);
      })
      .addCase(fetchTranscriptVersion.fulfilled, (state, {meta, payload}) => {
        try {
          transcriptssAdapter.removeOne(payload.id, state);
        } catch (error) {
          console.log('Nothing avaioable to delete');
        }
        transcriptssAdapter.setOne(state, payload);
      })
      .addCase(fetchTranscript.fulfilled, (state, {meta, payload}) => {
        try {
          transcriptssAdapter.removeOne(payload.id, state);
        } catch (error) {
          console.log('Nothing avaioable to delete');
        }
        transcriptssAdapter.setOne(state, payload);
      })
      // .addCase(updateTranscript.pending, removeRequestId)
      .addCase(updateTranscript.pending, (
        state,
        args
      ) => {
        const {meta: {arg: {id, words, startTime, endTime}}} = args;
        // The base array, not the draft: slicing the draft would create a
        // proxy per word (~35k on long files) on every save.
        const entity = state.entities[id];
        const baseEntity = isDraft(entity) ? original(entity) : entity;
        const oldWords = (baseEntity && baseEntity.words) || [];
        const preSliceIndex = _findIndex(oldWords, ({start_time}) => start_time >= startTime);
        const preSlice = oldWords.slice(0, preSliceIndex);
        const postSliceIndex = _findIndex(oldWords, ({start_time}) => start_time >= endTime);
        const postSlice = oldWords.slice(postSliceIndex);

        // make sure it isnt out of order before pesistint
        let lastStartTime = 0;
        const found = words.find(word => {
          if (word.start_time < lastStartTime) return true;
          else {
            lastStartTime = word.start_time;
            return false;
          }
        });

        !found && _set(state, ['entities', id, 'words'], Object.freeze([...preSlice, ...words, ...postSlice]));
        addRequestId(state, args);
      });
  }
});

const reducer = slice.reducer;
export default reducer;
